import { FillInQuestion } from '../../types';

export interface MatheFillInQuestion extends FillInQuestion {
  linkedLesson: string;
  explanation: string;
}

export const lessonOrder = [
  'ma-lektion-kugel-o',
  'ma-lektion-kugel-v',
  'ma-lektion-umkehr',
  'ma-lektion-zusammen',
  'ma-lektion-statistik1',
  'ma-lektion-statistik2',
  'ma-lektion-prozent',
];

export const fillInQuestions: MatheFillInQuestion[] = [
  // FillIn 1: Kugel O und V (8 Übungen)
  {
    id: 'ma-fi-1',
    category: 'kugel-ov',
    instruction: 'Ergänze die Formel.',
    sentence: 'O = _____ · π · r²',
    gaps: [{ acceptedAnswers: ['4'], position: 0 }],
    hint: 'Wie viele Kreisflächen?',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-kugel-o',
    explanation: 'O = 4·π·r²',
  },
  {
    id: 'ma-fi-2',
    category: 'kugel-ov',
    instruction: 'Ergänze die Formel mit Durchmesser.',
    sentence: 'O = _____² · π (mit Durchmesser)',
    gaps: [{ acceptedAnswers: ['d'], position: 0 }],
    hint: 'Welche Größe wird quadriert?',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-kugel-o',
    explanation: 'O = d²·π',
  },
  {
    id: 'ma-fi-3',
    category: 'kugel-ov',
    instruction: 'Ergänze die Volumen-Formel.',
    sentence: 'V = _____/3 · π · r³',
    gaps: [{ acceptedAnswers: ['4'], position: 0 }],
    hint: 'Vier-Drittel-Pi-r-drei',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-kugel-o',
    explanation: 'V = 4/3·π·r³',
  },
  {
    id: 'ma-fi-4',
    category: 'kugel-ov',
    instruction: 'Berechne den Radius.',
    sentence: 'd = 14 cm → r = _____ cm',
    gaps: [{ acceptedAnswers: ['7'], position: 0 }],
    hint: 'r = d ÷ 2',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-kugel-o',
    explanation: 'r = 14 ÷ 2 = 7 cm',
  },
  {
    id: 'ma-fi-5',
    category: 'kugel-ov',
    instruction: 'Berechne die Oberfläche Schritt für Schritt.',
    sentence: 'Kugel r = 3 cm: O = 4·π·_____ = _____·π ≈ 113,10 cm²',
    gaps: [
      { acceptedAnswers: ['9'], position: 0 },
      { acceptedAnswers: ['36'], position: 1 },
    ],
    hint: 'r² = 3² = 9, dann 4·9 = ?',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-kugel-o',
    explanation: 'O = 4·π·9 = 36π ≈ 113,10 cm²',
  },
  {
    id: 'ma-fi-6',
    category: 'kugel-ov',
    instruction: 'Berechne das Volumen Schritt für Schritt.',
    sentence: 'Kugel r = 3 cm: V = 4/3·π·_____ = _____·π ≈ 113,10 cm³',
    gaps: [
      { acceptedAnswers: ['27'], position: 0 },
      { acceptedAnswers: ['36'], position: 1 },
    ],
    hint: 'r³ = 3³ = 27, dann 4/3·27 = ?',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-kugel-o',
    explanation: 'V = 4/3·π·27 = 36π ≈ 113,10 cm³',
  },
  {
    id: 'ma-fi-7',
    category: 'kugel-ov',
    instruction: 'Berechne die Oberfläche.',
    sentence: 'Kugel r = 10 cm: O = _____·π ≈ _____ cm² (auf ganze gerundet)',
    gaps: [
      { acceptedAnswers: ['400'], position: 0 },
      { acceptedAnswers: ['1257'], position: 1 },
    ],
    hint: '4·100 = 400, 400·π ≈ ?',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-kugel-o',
    explanation: 'O = 400π ≈ 1256,64 ≈ 1257 cm²',
  },
  {
    id: 'ma-fi-8',
    category: 'kugel-ov',
    instruction: 'Ergänze die Einheiten als Hochzahl.',
    sentence: 'Einheit O: cm_____, Einheit V: cm_____',
    gaps: [
      { acceptedAnswers: ['2', '²'], position: 0 },
      { acceptedAnswers: ['3', '³'], position: 1 },
    ],
    hint: 'Fläche = ², Raum = ³',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-kugel-o',
    explanation: 'O → cm² (Fläche), V → cm³ (Raum)',
  },

  // FillIn 2: Umkehraufgaben (6 Übungen)
  {
    id: 'ma-fi-9',
    category: 'kugel-umkehr',
    instruction: 'Berechne d aus der Oberfläche.',
    sentence: 'O = 144π → d = √(144π÷π) = √_____ = _____ cm',
    gaps: [
      { acceptedAnswers: ['144'], position: 0 },
      { acceptedAnswers: ['12'], position: 1 },
    ],
    hint: 'π kürzt sich!',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-umkehr',
    explanation: 'd = √144 = 12 cm',
  },
  {
    id: 'ma-fi-10',
    category: 'kugel-umkehr',
    instruction: 'Berechne r aus der Oberfläche.',
    sentence: 'O = 144π → r = √(144π÷4π) = √_____ = _____ cm',
    gaps: [
      { acceptedAnswers: ['36'], position: 0 },
      { acceptedAnswers: ['6'], position: 1 },
    ],
    hint: '144÷4 = 36',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-umkehr',
    explanation: 'r = √36 = 6 cm',
  },
  {
    id: 'ma-fi-11',
    category: 'kugel-umkehr',
    instruction: 'Berechne r Schritt für Schritt.',
    sentence: 'O = 324π → r² = 324π÷(4π) = _____, r = _____ cm',
    gaps: [
      { acceptedAnswers: ['81'], position: 0 },
      { acceptedAnswers: ['9'], position: 1 },
    ],
    hint: '324÷4 = 81, √81 = ?',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-umkehr',
    explanation: 'r² = 81, r = 9 cm',
  },
  {
    id: 'ma-fi-12',
    category: 'kugel-umkehr',
    instruction: 'Berechne r aus dem Volumen.',
    sentence: 'V = 288π → r³ = 3·288÷4 = _____, r = _____ cm',
    gaps: [
      { acceptedAnswers: ['216'], position: 0 },
      { acceptedAnswers: ['6'], position: 1 },
    ],
    hint: '864÷4 = 216, ∛216 = ?',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-umkehr',
    explanation: 'r³ = 216, r = 6 cm',
  },
  {
    id: 'ma-fi-13',
    category: 'kugel-umkehr',
    instruction: 'Berechne r aus dem Volumen.',
    sentence: 'V = 36π → r³ = 3·36÷4 = _____, r = _____ cm',
    gaps: [
      { acceptedAnswers: ['27'], position: 0 },
      { acceptedAnswers: ['3'], position: 1 },
    ],
    hint: '108÷4 = 27, ∛27 = ?',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-umkehr',
    explanation: 'r³ = 27, r = 3 cm',
  },
  {
    id: 'ma-fi-14',
    category: 'kugel-umkehr',
    instruction: 'Berechne r und d aus der Oberfläche.',
    sentence: 'O = 400π → r = _____ cm, d = _____ cm',
    gaps: [
      { acceptedAnswers: ['10'], position: 0 },
      { acceptedAnswers: ['20'], position: 1 },
    ],
    hint: 'r² = 400÷4 = 100, r = 10, d = 2r',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-umkehr',
    explanation: 'r = 10 cm, d = 20 cm',
  },

  // FillIn 3: Zusammengesetzte Körper + Dichte (8 Übungen)
  {
    id: 'ma-fi-15',
    category: 'kugel-zusammen',
    instruction: 'Ergänze die Halbkugel-Formel.',
    sentence: 'V Halbkugel = _____/3 · π · r³',
    gaps: [{ acceptedAnswers: ['2'], position: 0 }],
    hint: 'Hälfte von 4/3',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-zusammen',
    explanation: 'V = 2/3·π·r³',
  },
  {
    id: 'ma-fi-16',
    category: 'kugel-zusammen',
    instruction: 'Ergänze die Viertelkugel-Formel.',
    sentence: 'V Viertelkugel = _____/3 · π · r³',
    gaps: [{ acceptedAnswers: ['1'], position: 0 }],
    hint: 'Viertel von 4/3',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-zusammen',
    explanation: 'V = 1/3·π·r³',
  },
  {
    id: 'ma-fi-17',
    category: 'kugel-zusammen',
    instruction: 'Wie berechnet man V gesamt?',
    sentence: 'V gesamt = V₁ _____ V₂',
    gaps: [{ acceptedAnswers: ['+', 'plus'], position: 0 }],
    hint: 'Volumen werden...',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-zusammen',
    explanation: 'V_ges = V₁ + V₂',
  },
  {
    id: 'ma-fi-18',
    category: 'kugel-zusammen',
    instruction: 'Was passiert mit der Berührungsfläche?',
    sentence: 'Die Berührungsfläche bei O wird _____.',
    gaps: [{ acceptedAnswers: ['nicht mitgerechnet', 'weggelassen', 'nicht gerechnet'], position: 0 }],
    hint: 'Die Fläche ist verdeckt...',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-zusammen',
    explanation: 'Berührungsfläche wird NICHT mitgerechnet.',
  },
  {
    id: 'ma-fi-19',
    category: 'kugel-zusammen',
    instruction: 'Berechne die Hohlkugel.',
    sentence: 'Hohlkugel R=5, r=3: R³−r³ = _____−_____ = _____',
    gaps: [
      { acceptedAnswers: ['125'], position: 0 },
      { acceptedAnswers: ['27'], position: 1 },
      { acceptedAnswers: ['98'], position: 2 },
    ],
    hint: '5³ = 125, 3³ = 27',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-zusammen',
    explanation: '125 − 27 = 98',
  },
  {
    id: 'ma-fi-20',
    category: 'kugel-zusammen',
    instruction: 'Berechne das Volumen.',
    sentence: 'Kugel r=6cm aus Eisen (ρ=7,8 g/cm³): V = _____·π ≈ _____ cm³ (gerundet)',
    gaps: [
      { acceptedAnswers: ['288'], position: 0 },
      { acceptedAnswers: ['905'], position: 1 },
    ],
    hint: 'r³=216, 4/3·216=288, 288π≈905',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-zusammen',
    explanation: 'V = 288π ≈ 904,78 ≈ 905 cm³',
  },
  {
    id: 'ma-fi-21',
    category: 'kugel-zusammen',
    instruction: 'Berechne die Masse.',
    sentence: 'Masse = _____·_____ = 905·7,8 ≈ _____ g (gerundet)',
    gaps: [
      { acceptedAnswers: ['V'], position: 0 },
      { acceptedAnswers: ['ρ', 'rho', 'Dichte'], position: 1 },
      { acceptedAnswers: ['7059'], position: 2 },
    ],
    hint: 'm = V · ρ',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-zusammen',
    explanation: 'm = 905 · 7,8 ≈ 7059 g',
  },
  {
    id: 'ma-fi-22',
    category: 'kugel-zusammen',
    instruction: 'Berechne das Schneemann-Volumen.',
    sentence: 'Schneemann r₁=10, r₂=25, r₃=50cm. V_ges = 4/3π(_____ + _____ + _____)',
    gaps: [
      { acceptedAnswers: ['1000'], position: 0 },
      { acceptedAnswers: ['15625'], position: 1 },
      { acceptedAnswers: ['125000'], position: 2 },
    ],
    hint: '10³, 25³, 50³',
    difficulty: 'bonus',
    linkedLesson: 'ma-lektion-zusammen',
    explanation: '10³=1000, 25³=15625, 50³=125000',
  },

  // FillIn 4: Statistik Häufigkeiten & Mittelwerte (mit Zwischenschritten)
  {
    id: 'ma-fi-23',
    category: 'statistik-haeufigkeit',
    instruction: 'Erstelle die Rangliste und bestimme n.',
    sentence: 'Urliste: 4, 8, 2, 6, 10\nRangliste: _____\nn = _____',
    gaps: [
      { acceptedAnswers: ['2, 4, 6, 8, 10', '2,4,6,8,10'], position: 0 },
      { acceptedAnswers: ['5'], position: 1 },
    ],
    hint: 'Aufsteigend sortieren, dann zählen',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-statistik1',
    explanation: 'Rangliste: 2, 4, 6, 8, 10 — n = 5',
  },
  {
    id: 'ma-fi-24',
    category: 'statistik-haeufigkeit',
    instruction: 'Berechne den Durchschnitt Schritt für Schritt.',
    sentence: 'Werte: 4, 8, 2, 6, 10\nSchritt 1: Summe = _____\nSchritt 2: x̄ = Summe ÷ n = _____ ÷ 5 = _____',
    gaps: [
      { acceptedAnswers: ['30'], position: 0 },
      { acceptedAnswers: ['30'], position: 1 },
      { acceptedAnswers: ['6'], position: 2 },
    ],
    hint: 'Alle Werte addieren, dann durch Anzahl teilen',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-statistik1',
    explanation: 'Summe = 30, x̄ = 30 ÷ 5 = 6',
  },
  {
    id: 'ma-fi-25',
    category: 'statistik-haeufigkeit',
    instruction: 'Bestimme den Zentralwert Z Schritt für Schritt.',
    sentence: 'Rangliste: 2, 4, 6, 8, 10\nn = _____ (ungerade/gerade?) → _____\nPosition: (n+1)÷2 = _____. Wert\nZ = _____',
    gaps: [
      { acceptedAnswers: ['5'], position: 0 },
      { acceptedAnswers: ['ungerade'], position: 1 },
      { acceptedAnswers: ['3'], position: 2 },
      { acceptedAnswers: ['6'], position: 3 },
    ],
    hint: 'n=5 → ungerade → der mittlere Wert',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-statistik1',
    explanation: 'n=5 (ungerade) → 3. Wert → Z = 6',
  },
  {
    id: 'ma-fi-26',
    category: 'statistik-haeufigkeit',
    instruction: 'Bestimme Z bei gerader Anzahl Schritt für Schritt.',
    sentence: 'Rangliste: 3, 5, 7, 9\nn = _____ (gerade) → Durchschnitt der beiden mittleren Werte\nZ = (_____ + _____) ÷ 2 = _____',
    gaps: [
      { acceptedAnswers: ['4'], position: 0 },
      { acceptedAnswers: ['5'], position: 1 },
      { acceptedAnswers: ['7'], position: 2 },
      { acceptedAnswers: ['6'], position: 3 },
    ],
    hint: 'n=4 → gerade → 2. und 3. Wert mitteln',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-statistik1',
    explanation: 'n=4 (gerade) → Z = (5+7)÷2 = 6',
  },
  {
    id: 'ma-fi-27',
    category: 'statistik-haeufigkeit',
    instruction: 'Bestimme den Modalwert.',
    sentence: 'Urliste: 2, 3, 3, 5, 7, 7, 7, 9\nWelcher Wert kommt am häufigsten vor?\nm = _____ (kommt _____× vor)',
    gaps: [
      { acceptedAnswers: ['7'], position: 0 },
      { acceptedAnswers: ['3'], position: 1 },
    ],
    hint: 'Zähle wie oft jeder Wert vorkommt',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-statistik1',
    explanation: '3 kommt 2×, 7 kommt 3× → m = 7',
  },
  {
    id: 'ma-fi-28',
    category: 'statistik-haeufigkeit',
    instruction: 'Berechne die Häufigkeiten Schritt für Schritt.',
    sentence: '12 von 30 Schülern:\nrelative H. = 12 ÷ 30 = _____\nprozentuelle H. = _____ × 100 = _____%',
    gaps: [
      { acceptedAnswers: ['0,4', '0.4'], position: 0 },
      { acceptedAnswers: ['0,4', '0.4'], position: 1 },
      { acceptedAnswers: ['40'], position: 2 },
    ],
    hint: 'Erst relativ (÷ Gesamtanzahl), dann × 100',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-statistik1',
    explanation: 'rel. = 0,4 → proz. = 40 %',
  },
  {
    id: 'ma-fi-29',
    category: 'statistik-haeufigkeit',
    instruction: 'Berechne die absolute Häufigkeit.',
    sentence: 'Von 400 Kindern haben 70 % ein Smartphone.\nrelative H. = 70 ÷ 100 = _____\nabsolute H. = 400 × _____ = _____ Kinder',
    gaps: [
      { acceptedAnswers: ['0,7', '0.7', '0,70', '0.70'], position: 0 },
      { acceptedAnswers: ['0,7', '0.7', '0,70', '0.70'], position: 1 },
      { acceptedAnswers: ['280'], position: 2 },
    ],
    hint: 'Prozent → relativ → absolut',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-statistik1',
    explanation: '0,7 × 400 = 280 Kinder',
  },
  {
    id: 'ma-fi-30',
    category: 'statistik-haeufigkeit',
    instruction: 'Bestimme die Klassenanzahl und Klassenbreite.',
    sentence: '36 Messwerte, Min = 12, Max = 60\nKlassenanzahl ≈ √_____ = _____\nKlassenbreite = (_____ − _____) ÷ _____ = _____',
    gaps: [
      { acceptedAnswers: ['36'], position: 0 },
      { acceptedAnswers: ['6'], position: 1 },
      { acceptedAnswers: ['60'], position: 2 },
      { acceptedAnswers: ['12'], position: 3 },
      { acceptedAnswers: ['6'], position: 4 },
      { acceptedAnswers: ['8'], position: 5 },
    ],
    hint: 'Faustformel: √n, dann (Max−Min) ÷ Klassen',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-statistik1',
    explanation: '√36=6 Klassen, Breite = (60−12) ÷ 6 = 8',
  },
  {
    id: 'ma-fi-31',
    category: 'statistik-haeufigkeit',
    instruction: 'Berechne das gewogene Mittel Schritt für Schritt.',
    sentence: 'Noten: 1×2, 2×5, 3×8, 4×3, 5×2\nn = 2+5+8+3+2 = _____\nSumme = 1·2 + 2·5 + 3·8 + 4·3 + 5·2 = 2+10+24+12+10 = _____\nx̄ = _____ ÷ _____ = _____',
    gaps: [
      { acceptedAnswers: ['20'], position: 0 },
      { acceptedAnswers: ['58'], position: 1 },
      { acceptedAnswers: ['58'], position: 2 },
      { acceptedAnswers: ['20'], position: 3 },
      { acceptedAnswers: ['2,9', '2.9'], position: 4 },
    ],
    hint: 'Note × Häufigkeit, dann Summe ÷ n',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-statistik1',
    explanation: '58 ÷ 20 = 2,9',
  },
  {
    id: 'ma-fi-32',
    category: 'statistik-haeufigkeit',
    instruction: 'Bestimme den Modalwert.',
    sentence: 'Preise: 60ct kommt 2×, 65ct kommt 2×, 66ct kommt 2×.\nModalwert = _____',
    gaps: [{ acceptedAnswers: ['60, 65 und 66', '60, 65, 66', '60 und 65 und 66', 'alle drei'], position: 0 }],
    hint: 'Alle drei kommen gleich oft vor!',
    difficulty: 'bonus',
    linkedLesson: 'ma-lektion-statistik1',
    explanation: 'Drei Modalwerte: 60, 65, 66 (je 2×)',
  },
  {
    id: 'ma-fi-32b',
    category: 'statistik-haeufigkeit',
    instruction: 'Bestimme Z bei einer größeren Rangliste.',
    sentence: 'Rangliste (n=12): 68; 69; 71; 72; 74; 75; 78; 79; 80; 81; 84; 85\nn = _____ (gerade) → _____ und _____. Wert\nZ = (_____ + _____) ÷ 2 = _____',
    gaps: [
      { acceptedAnswers: ['12'], position: 0 },
      { acceptedAnswers: ['6', '6.'], position: 1 },
      { acceptedAnswers: ['7', '7.'], position: 2 },
      { acceptedAnswers: ['75'], position: 3 },
      { acceptedAnswers: ['78'], position: 4 },
      { acceptedAnswers: ['76,5', '76.5'], position: 5 },
    ],
    hint: 'Bei n=12: 6. und 7. Wert mitteln',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-statistik1',
    explanation: 'Z = (75 + 78) ÷ 2 = 76,5',
  },
  {
    id: 'ma-fi-32c',
    category: 'statistik-haeufigkeit',
    instruction: 'Berechne den Durchschnitt der Schuhgrößen.',
    sentence: 'Schuhgrößen: 37, 37, 38, 40, 42\nn = _____\nSumme = _____\nx̄ = _____ ÷ _____ = _____',
    gaps: [
      { acceptedAnswers: ['5'], position: 0 },
      { acceptedAnswers: ['194'], position: 1 },
      { acceptedAnswers: ['194'], position: 2 },
      { acceptedAnswers: ['5'], position: 3 },
      { acceptedAnswers: ['38,8', '38.8'], position: 4 },
    ],
    hint: 'Alle addieren, durch Anzahl teilen',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-statistik1',
    explanation: '194 ÷ 5 = 38,8',
  },

  // FillIn 5: Streuung & Diagramme (erweitert mit Zwischenschritten)
  {
    id: 'ma-fi-33',
    category: 'statistik-streuung',
    instruction: 'Berechne die Spannweite Schritt für Schritt.',
    sentence: 'Rangliste: 3; 7; 17; 17; 17; 19; 19; 34; 35; 44\nMinimum = _____\nMaximum = _____\nR = _____ − _____ = _____',
    gaps: [
      { acceptedAnswers: ['3'], position: 0 },
      { acceptedAnswers: ['44'], position: 1 },
      { acceptedAnswers: ['44'], position: 2 },
      { acceptedAnswers: ['3'], position: 3 },
      { acceptedAnswers: ['41'], position: 4 },
    ],
    hint: 'R = Max − Min',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-statistik2',
    explanation: 'R = 44 − 3 = 41',
  },
  {
    id: 'ma-fi-34',
    category: 'statistik-streuung',
    instruction: 'Berechne die Halbweite Schritt für Schritt.',
    sentence: 'q₁ = 27, q₃ = 74,5\nHalbweite r = q₃ − q₁ = _____ − _____ = _____',
    gaps: [
      { acceptedAnswers: ['74,5', '74.5'], position: 0 },
      { acceptedAnswers: ['27'], position: 1 },
      { acceptedAnswers: ['47,5', '47.5'], position: 2 },
    ],
    hint: 'q₃ − q₁',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-statistik2',
    explanation: 'r = 74,5 − 27 = 47,5',
  },
  {
    id: 'ma-fi-35',
    category: 'statistik-streuung',
    instruction: 'Berechne die Kreisdiagramm-Winkel.',
    sentence: '1 % = _____°\n25 % = _____ × 3,6° = _____°\n40 % = _____ × 3,6° = _____°',
    gaps: [
      { acceptedAnswers: ['3,6', '3.6'], position: 0 },
      { acceptedAnswers: ['25'], position: 1 },
      { acceptedAnswers: ['90'], position: 2 },
      { acceptedAnswers: ['40'], position: 3 },
      { acceptedAnswers: ['144'], position: 4 },
    ],
    hint: '360° ÷ 100 = 3,6° pro Prozent',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-statistik2',
    explanation: '1%=3,6°, 25%=90°, 40%=144°',
  },
  {
    id: 'ma-fi-36',
    category: 'statistik-streuung',
    instruction: 'Ergänze den Boxplot.',
    sentence: 'Boxplot: Die Box geht von _____ bis _____.\nDer Strich in der Box = _____.\nDie Whisker gehen bis _____ und _____.',
    gaps: [
      { acceptedAnswers: ['q1', 'q₁'], position: 0 },
      { acceptedAnswers: ['q3', 'q₃'], position: 1 },
      { acceptedAnswers: ['Median', 'Z', 'Zentralwert'], position: 2 },
      { acceptedAnswers: ['Min', 'Minimum'], position: 3 },
      { acceptedAnswers: ['Max', 'Maximum'], position: 4 },
    ],
    hint: 'Box = Quartile, Strich = Median, Antennen = Min/Max',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-statistik2',
    explanation: 'Box: q₁ bis q₃, Strich = Median, Whisker = Min und Max',
  },
  {
    id: 'ma-fi-36b',
    category: 'statistik-streuung',
    instruction: 'Berechne dz Schritt für Schritt.',
    sentence: 'Werte: 2, 4, 6, 8, 10. Z = 6, n = 5.\ndz = (|2−6| + |4−6| + |6−6| + |8−6| + |10−6|) ÷ 5\n= (_____ + _____ + 0 + _____ + _____) ÷ 5\n= _____ ÷ 5 = _____',
    gaps: [
      { acceptedAnswers: ['4'], position: 0 },
      { acceptedAnswers: ['2'], position: 1 },
      { acceptedAnswers: ['2'], position: 2 },
      { acceptedAnswers: ['4'], position: 3 },
      { acceptedAnswers: ['12'], position: 4 },
      { acceptedAnswers: ['2,4', '2.4'], position: 5 },
    ],
    hint: 'Jeden Abstand zum Zentralwert berechnen, summieren, durch n teilen',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-statistik2',
    explanation: 'dz = 12 ÷ 5 = 2,4',
  },
  {
    id: 'ma-fi-36c',
    category: 'statistik-streuung',
    instruction: 'Berechne den Streubereich um Z.',
    sentence: 'Z = 65, dz = 4,18\nStreubereich: Z ± dz = 65 ± 4,18\nvon _____ bis _____',
    gaps: [
      { acceptedAnswers: ['60,82', '60.82'], position: 0 },
      { acceptedAnswers: ['69,18', '69.18'], position: 1 },
    ],
    hint: '65 − 4,18 = ? und 65 + 4,18 = ?',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-statistik2',
    explanation: '65 − 4,18 = 60,82 bis 65 + 4,18 = 69,18',
  },
  {
    id: 'ma-fi-36d',
    category: 'statistik-streuung',
    instruction: 'Berechne sx Schritt für Schritt.',
    sentence: 'Werte: 2, 4, 6, 8, 10. x̄ = 6, n = 5.\nsx² = [(2−6)² + (4−6)² + (6−6)² + (8−6)² + (10−6)²] ÷ 5\n= [16 + _____ + 0 + _____ + 16] ÷ 5\n= _____ ÷ 5 = _____\nsx = √_____ ≈ _____',
    gaps: [
      { acceptedAnswers: ['4'], position: 0 },
      { acceptedAnswers: ['4'], position: 1 },
      { acceptedAnswers: ['40'], position: 2 },
      { acceptedAnswers: ['8'], position: 3 },
      { acceptedAnswers: ['8'], position: 4 },
      { acceptedAnswers: ['2,83', '2.83', '2,8', '2.8'], position: 5 },
    ],
    hint: 'Quadrieren, summieren, durch n, Wurzel',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-statistik2',
    explanation: 'sx = √8 ≈ 2,83',
  },
  {
    id: 'ma-fi-36e',
    category: 'statistik-streuung',
    instruction: 'Bestimme q₁ und q₃.',
    sentence: 'Rangliste (n=20): 59;59;59;59;60;61;61;63;64;66;68;68;68;69;70;71;71;73;78;82\nZ = (66+68)÷2 = _____\nUntere Hälfte (Pos. 1–10): q₁ = (60+61)÷2 = _____\nObere Hälfte (Pos. 11–20): q₃ = (70+71)÷2 = _____',
    gaps: [
      { acceptedAnswers: ['67'], position: 0 },
      { acceptedAnswers: ['60,5', '60.5'], position: 1 },
      { acceptedAnswers: ['70,5', '70.5'], position: 2 },
    ],
    hint: 'Rangliste halbieren, Median jeder Hälfte = Quartil',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-statistik2',
    explanation: 'Z=67, q₁=60,5, q₃=70,5',
  },

  // FillIn 6: Prozentrechnung (4 Übungen)
  {
    id: 'ma-fi-37',
    category: 'prozent',
    instruction: 'Berechne den Anteil.',
    sentence: '15% von 300€: A = 300·_____÷100 = _____€',
    gaps: [
      { acceptedAnswers: ['15'], position: 0 },
      { acceptedAnswers: ['45'], position: 1 },
    ],
    hint: 'A = G·p÷100',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-prozent',
    explanation: 'A = 300·15÷100 = 45€',
  },
  {
    id: 'ma-fi-38',
    category: 'prozent',
    instruction: 'Berechne den Grundwert.',
    sentence: '60€ sind 25% von _____€',
    gaps: [{ acceptedAnswers: ['240'], position: 0 }],
    hint: 'G = A·100÷p',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-prozent',
    explanation: 'G = 60·100÷25 = 240€',
  },
  {
    id: 'ma-fi-39',
    category: 'prozent',
    instruction: 'Berechne den Prozentsatz.',
    sentence: '24 von 80 → p = _____%',
    gaps: [{ acceptedAnswers: ['30'], position: 0 }],
    hint: 'p = A·100÷G',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-prozent',
    explanation: 'p = 24·100÷80 = 30%',
  },
  {
    id: 'ma-fi-40',
    category: 'prozent',
    instruction: 'Berechne den neuen Preis.',
    sentence: 'Pullover 120€, 30% Rabatt → neuer Preis = _____€',
    gaps: [{ acceptedAnswers: ['84'], position: 0 }],
    hint: '120 − (120·30÷100)',
    difficulty: 'pflicht',
    linkedLesson: 'ma-lektion-prozent',
    explanation: '120 − 36 = 84€',
  },
];
